const DIRTY_CLASS = 'has-unsaved-changes'
const DIRTY_EVENT = 'ui-form-dirty-change'
const COMMIT_EVENT = 'ui-form-commit-success'
const FIELD_GUIDE_EVENT = 'ui-form-field-guide'
const GUIDE_ARRIVED_EVENT = 'ui-form-guide-arrived'
const IMAGE_CHANGE_EVENT = 'ui-image-upload-change'
const COMMIT_ACTIONS = ['create', 'save']
const FIELD_SELECTOR = 'input, textarea, select, [contenteditable="true"], [role="switch"], [role="slider"], [role="combobox"]'
const COMPOSITE_CONTROL_SELECTOR = [
  '.el-input-number',
  '.el-switch',
  '.el-select',
  '.el-date-editor',
  '.el-color-picker',
  '.el-checkbox',
  '.el-radio',
  '.el-radio-group',
  '.el-slider',
  '.el-transfer',
  '.el-rate',
  '.el-upload'
].join(', ')
const formBaselines = new WeakMap()
const fieldBaselines = new WeakMap()
const scopeDirtyStates = new WeakMap()
let pendingScope = null
let activeFieldScope = null
let activeFieldControl = null
let uploadObserver = null
let uploadCompareTimer = null

function isVisible (element) {
  return Boolean(element && (element.offsetWidth || element.offsetHeight || element.getClientRects().length))
}

function requiredItemIsComplete (item) {
  if (!isVisible(item) || item.classList.contains('is-disabled')) return true
  if (item.classList.contains('is-error') || item.querySelector('.el-form-item__error')) return false

  const itemFields = Array.from(item.querySelectorAll(FIELD_SELECTOR))
  const fields = itemFields.filter(field => !field.disabled && field.type !== 'hidden')
  // 编辑页常把已经由后端确认的必填字段设为只读/禁用（如飞机注册日期）。
  // 它们不参与本次提交校验，不能因此阻断整张表单的修改引导动画。
  if (itemFields.length && !fields.length) return true
  if (fields.some(field => field.matches('[role="switch"], [role="slider"]'))) return true

  const checks = fields.filter(field => field.type === 'checkbox' || field.type === 'radio')
  if (checks.length) return checks.some(field => field.checked)

  const valueFields = fields.filter(field => !['button', 'submit', 'reset', 'file'].includes(field.type))
  if (valueFields.length) {
    return valueFields.every(field => {
      if (field.matches('[contenteditable="true"]')) return field.textContent.trim() !== ''
      return String(field.value == null ? '' : field.value).trim() !== ''
    })
  }

  // 上传类必填项以已展示的图片或文件列表为完成标志。
  if (item.querySelector('.avatar, .el-upload-list__item:not(.is-ready), img[src]')) return true
  return false
}

function scopeIsComplete (scope) {
  const hasVisibleError = Array.from(scope.querySelectorAll('.el-form-item.is-error'))
    .some(isVisible)
  if (hasVisibleError) return false
  return Array.from(scope.querySelectorAll('.el-form-item.is-required'))
    .every(requiredItemIsComplete)
}

function setScopeDirty (scope, dirty) {
  const eligible = dirty && scopeIsComplete(scope)
  scope.classList.toggle(DIRTY_CLASS, eligible)
  window.dispatchEvent(new CustomEvent(DIRTY_EVENT, { detail: { scope, dirty: eligible } }))
}

function findFormScope (element, requireCommitAction = false) {
  if (!element || !element.closest) return null
  // 优先选择同时包含表单和标题栏按钮的稳定容器，避免只标记到 el-main 内部。
  const selectors = ['.form-dirty-scope', '.el-dialog', '.el-drawer', '.el-card', '.el-container', '.app-container', '.el-main']
  for (const selector of selectors) {
    const scope = element.closest(selector)
    if (scope && (!requireCommitAction || Array.from(scope.querySelectorAll('[data-ui-action="create"], [data-ui-action="save"]')).some(isVisible))) return scope
  }
  return null
}

function findFieldScope (event) {
  const field = findEventControl(event)
  // 筛选、预览切换不是待提交字段，不能借用页面里其他表单的按钮。
  if (!field || !field.closest('.el-form-item, .route-design-popup-row, .form-dirty-scope')) return null
  return findFormScope(field, true)
}

function findEventControl (event) {
  const target = event.target
  if (!target || !target.closest) return null
  const imageUpload = target.closest('.ui-image-upload')
  if (imageUpload) return imageUpload
  // 穿梭框内部还有 checkbox、input 等复合控件，必须优先归一到整个 transfer，
  // 否则候选项勾选会被误判成独立复选框的表单值变化。
  const transfer = target.closest('.el-transfer')
  if (transfer) return transfer
  return target.closest(COMPOSITE_CONTROL_SELECTOR) || target.closest(FIELD_SELECTOR)
}

function serializeScope (scope) {
  // 穿梭框内部的勾选框和搜索框只是临时操作状态，不属于最终表单值。
  const fields = Array.from(scope.querySelectorAll(FIELD_SELECTOR))
    .filter(field => !field.closest('.el-transfer, .ui-image-upload'))
    .map((field, index) => `${index}:${serializeField(field)}`)
  const visualControls = Array.from(scope.querySelectorAll('.el-color-picker, .el-rate, .el-upload, .el-transfer, .ui-image-upload'))
    .filter(field => field.matches('.ui-image-upload') || !field.closest('.ui-image-upload'))
    .map((field, index) => `visual-${index}:${serializeField(field)}`)
  return fields.concat(visualControls).join('|')
}

function serializeField (field) {
  if (!field.matches(FIELD_SELECTOR)) {
    if (field.matches('.ui-image-upload')) return `upload:${field.getAttribute('data-value') || '[]'}`
    if (field.matches('.el-transfer')) return `transfer:${field.getAttribute('data-value') || '[]'}`
    if (field.matches('.el-color-picker')) {
      const swatch = field.querySelector('.el-color-picker__color-inner')
      return `color:${swatch ? swatch.style.backgroundColor : ''}`
    }
    if (field.matches('.el-rate')) {
      return `rate:${Array.from(field.querySelectorAll('.el-rate__icon')).map(icon => `${icon.className}:${icon.style.color}`).join('|')}`
    }
    if (field.matches('.el-upload')) {
      const item = field.closest('.el-form-item') || field.parentElement
      const images = Array.from(item.querySelectorAll('img[src]')).map(image => image.getAttribute('src'))
      const files = Array.from(item.querySelectorAll('.el-upload-list__item-name')).map(file => file.textContent.trim())
      return `upload:${images.concat(files).join('|')}`
    }
    return Array.from(field.querySelectorAll(FIELD_SELECTOR)).map(serializeField).join('|')
  }
  if (field.matches('[contenteditable="true"]')) return `html:${field.innerHTML}`
  if (field.matches('[role="switch"]')) return `switch:${field.getAttribute('aria-checked')}`
  if (field.matches('[role="slider"]')) return `slider:${field.getAttribute('aria-valuenow')}`
  if (field.matches('[role="combobox"]')) return `combobox:${field.getAttribute('data-value') || ''}`
  if (field.type === 'checkbox' || field.type === 'radio') return `checked:${field.checked ? 1 : 0}`
  return `value:${field.value == null ? '' : field.value}`
}

function captureBaseline (event) {
  const scope = findFieldScope(event)
  if (!scope) return
  activeFieldScope = scope
  if (!formBaselines.has(scope)) formBaselines.set(scope, serializeScope(scope))
  const field = findEventControl(event)
  if (field) {
    activeFieldControl = field
    fieldBaselines.set(field, serializeField(field))
  }
}

function compareWithBaseline (event) {
  const eventControl = findEventControl(event)
  // 图片选择、进度、预览和取消不改变业务值，只接受控件的成功/删除通知。
  if (eventControl && eventControl.matches('.ui-image-upload') && event.type !== IMAGE_CHANGE_EVENT) return
  // 穿梭框只有左右移动按钮会提交 v-model；候选勾选和搜索只是临时状态。
  // 将比较固定在移动按钮的 click 上，避免上一次值变化延迟到下一次勾选时补发粒子。
  if (eventControl && eventControl.matches('.el-transfer')) {
    const transferButton = event.type === 'click' && event.target && event.target.closest
      ? event.target.closest('.el-transfer__button')
      : null
    if (!transferButton) return
  }
  const scope = findFieldScope(event) || (!eventControl && activeFieldScope && activeFieldScope.isConnected ? activeFieldScope : null)
  if (!scope) return
  if (!formBaselines.has(scope)) formBaselines.set(scope, serializeScope(scope))
  window.setTimeout(() => {
    const changed = serializeScope(scope) !== formBaselines.get(scope)
    scopeDirtyStates.set(scope, changed)
    const field = eventControl || (activeFieldControl && activeFieldControl.isConnected ? activeFieldControl : null)
    const complete = scopeIsComplete(scope)
    let fieldChanged = false
    if (field) {
      const currentValue = serializeField(field)
      const previousValue = fieldBaselines.get(field)
      fieldChanged = previousValue != null && currentValue !== previousValue
      fieldBaselines.set(field, currentValue)
    }
    if (!changed || !complete) {
      setScopeDirty(scope, false)
      return
    }
    if (field && fieldChanged) {
      window.dispatchEvent(new CustomEvent(FIELD_GUIDE_EVENT, { detail: { scope, field, dirty: true } }))
    }
  }, 80)
}

function handleGuideArrived (event) {
  const scope = event.detail && event.detail.scope
  if (!scope || !scope.isConnected) return
  setScopeDirty(scope, scopeDirtyStates.get(scope) === true)
}

function handleUploadMutation () {
  if (!activeFieldControl || !activeFieldControl.isConnected || !activeFieldControl.matches('.el-upload')) return
  if (uploadCompareTimer) window.clearTimeout(uploadCompareTimer)
  uploadCompareTimer = window.setTimeout(() => {
    uploadCompareTimer = null
    compareWithBaseline({ target: activeFieldControl })
  }, 80)
}

function handleActionClick (event) {
  const button = event.target && event.target.closest
    ? event.target.closest('[data-ui-action]')
    : null
  if (!button) return

  const action = button.getAttribute('data-ui-action')
  const scope = findFormScope(button)
  if (!scope) return

  if (COMMIT_ACTIONS.includes(action)) pendingScope = scope
  if (action === 'reset') {
    window.setTimeout(() => {
      const baseline = formBaselines.get(scope)
      setScopeDirty(scope, baseline != null && serializeScope(scope) !== baseline)
    }, 0)
  }
}

export function installFormDirtyAura () {
  document.addEventListener(IMAGE_CHANGE_EVENT, event => {
    if (event.detail && event.detail.phase === 'before') captureBaseline(event)
    else if (event.detail && event.detail.phase === 'after') compareWithBaseline(event)
  }, true)
  document.addEventListener('pointerdown', captureBaseline, true)
  document.addEventListener('focusin', captureBaseline, true)
  document.addEventListener('keydown', captureBaseline, true)
  document.addEventListener('input', compareWithBaseline, true)
  document.addEventListener('change', compareWithBaseline, true)
  document.addEventListener('click', compareWithBaseline, true)
  document.addEventListener('keyup', compareWithBaseline, true)
  document.addEventListener('click', handleActionClick, true)
  window.addEventListener(GUIDE_ARRIVED_EVENT, handleGuideArrived)
  if (!uploadObserver && window.MutationObserver) {
    uploadObserver = new window.MutationObserver(handleUploadMutation)
    uploadObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['src'] })
  }
}

export function clearPendingFormDirtyAura () {
  if (!pendingScope) return
  const committedScope = pendingScope
  setScopeDirty(pendingScope, false)
  scopeDirtyStates.set(pendingScope, false)
  formBaselines.set(pendingScope, serializeScope(pendingScope))
  pendingScope = null
  window.dispatchEvent(new CustomEvent(COMMIT_EVENT, { detail: { scope: committedScope } }))
}

export function activateFormDirtyAura (scope) {
  if (!scope || !scope.isConnected) return false
  scopeDirtyStates.set(scope, true)
  setScopeDirty(scope, true)
  return scope.classList.contains(DIRTY_CLASS)
}

export function resetFormDirtyAura (scope) {
  if (!scope || !scope.isConnected) return
  formBaselines.set(scope, serializeScope(scope))
  scopeDirtyStates.set(scope, false)
  setScopeDirty(scope, false)
  if (pendingScope === scope) pendingScope = null
  window.dispatchEvent(new CustomEvent(COMMIT_EVENT, { detail: { scope } }))
}

export function releasePendingFormDirtyAura () {
  pendingScope = null
}
